import React, { useContext } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { UsuarioContext } from '../../providers/personaContext';
import { isSuperAdmin } from './auth';
import { useTiposNavegables } from './hooks/useTiposNavegables';
import { rutaTipo } from './utils/rutaTipo';
import Dashboard from './pages/Dashboard';

/**
 * Control de acceso del módulo Inventario a nivel de routing (II.6 Capa 1).
 *
 * - useInvPermisos(): helper { can, superAdmin } leyendo de UsuarioContext.
 * - <InvGuard>: protege una sección; si el usuario no tiene el permiso,
 *   redirige a la landing del módulo (que lo enviará a una sección que SÍ
 *   pueda ver). Nunca redirige a una sección sin permiso → sin bucles.
 * - <InventarioLanding>: ruta índice (/inventario). Si tiene dashboard.view
 *   muestra el Dashboard; si no, redirige a la primera sección permitida.
 * - <InvTipoGuard>: protege las rutas :slug y :slug/:id por can_view (Capa 2).
 */

export function useInvPermisos() {
  const ctx = useContext(UsuarioContext) || {};
  const permisos = Array.isArray(ctx.permisos) ? ctx.permisos : [];
  const superAdmin = isSuperAdmin();
  const can = (clave) => superAdmin || permisos.includes(clave);
  // `permisosCargados` lo expone el provider tras resolver GET /permisos/mios.
  // Si el provider aún no lo expone (versión previa), asumimos true para no
  // bloquear (retrocompat). Super Admin no necesita esperar (va por el JWT).
  const permisosCargados = superAdmin || ctx.permisosCargados !== false;
  return { can, superAdmin, permisosCargados };
}

function InvSkeleton() {
  return (
    <div className="bg-slate-50 px-6 py-5 min-h-full">
      <div className="animate-pulse space-y-3">
        <div className="h-7 w-64 bg-slate-100 rounded" />
        <div className="h-4 w-80 bg-slate-100 rounded" />
        <div className="h-32 w-full bg-slate-100 rounded mt-4" />
      </div>
    </div>
  );
}

function MensajeSinAcceso({ texto }) {
  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">
      <div className="border border-[hsl(var(--card-border))] rounded-lg p-4 bg-white">
        <p className="text-sm text-slate-600">{texto}</p>
      </div>
    </div>
  );
}

/**
 * Guard de sección. Uso:
 *   <InvGuard anyOf={['inventario.tipos.view']}><TiposList /></InvGuard>
 *   <InvGuard superOnly><Ajustes /></InvGuard>
 */
export function InvGuard({ anyOf = null, superOnly = false, children }) {
  const { can, superAdmin, permisosCargados } = useInvPermisos();

  // Esperar a que lleguen los permisos antes de decidir; si no, en la
  // primera pasada (permisos=[]) rebotaríamos por error → parpadeo.
  if (!permisosCargados) {
    return <InvSkeleton />;
  }

  let ok = superAdmin;
  if (!ok && !superOnly && Array.isArray(anyOf)) {
    ok = anyOf.some((clave) => can(clave));
  }
  if (!ok) {
    // A la landing del módulo: decidirá a qué sección permitida enviar.
    return <Navigate to="/inventario" replace />;
  }
  return children;
}

/**
 * Landing del módulo (ruta índice /inventario).
 * Orden de prioridad de la sección de aterrizaje:
 *   1. dashboard.view → Dashboard (in situ)
 *   2. ¿tiene tipos navegables? → primer tipo (/inventario/<slug>)
 *   3. tipos.view     → /inventario/tipos
 *   4. stock-entries.view → /inventario/compras
 *   5. eventos.view   → /inventario/historial
 *   6. (nada)         → mensaje
 *
 * "Tipos navegables" = los que el usuario puede ver según inv_type_access
 * (Capa 2). Ya no depende de la clave plana activos.view.
 */
export function InventarioLanding() {
  const { can, permisosCargados } = useInvPermisos();
  const { tipos, loading } = useTiposNavegables();

  // Esperar permisos: si no, la primera pasada (permisos=[]) caería en el
  // mensaje "sin acceso" un instante antes de redirigir → parpadeo.
  if (!permisosCargados) {
    return <InvSkeleton />;
  }

  if (can('inventario.dashboard.view')) {
    return <Dashboard />;
  }

  // Sin dashboard: si tiene algún tipo navegable, al primero.
  if (loading) {
    return <MensajeSinAcceso texto="Cargando inventario…" />;
  }
  if (Array.isArray(tipos) && tipos.length > 0) {
    return <Navigate to={rutaTipo(tipos[0].scope, tipos[0].slug)} replace />;
  }

  if (can('inventario.tipos.view')) {
    return <Navigate to="/inventario/tipos" replace />;
  }
  if (can('inventario.stock-entries.view')) {
    return <Navigate to="/inventario/compras" replace />;
  }
  if (can('inventario.eventos.view')) {
    return <Navigate to="/inventario/historial" replace />;
  }

  return (
    <MensajeSinAcceso texto="No tienes acceso a ninguna sección del inventario." />
  );
}

/**
 * Guard para las rutas de activos de un tipo
 * (/inventario/<ambito>/:slug y .../:id).
 *
 * Comprueba que el par (ámbito, slug) esté entre los tipos NAVEGABLES del
 * usuario (can_view, Capa 2). Si no, rebota a la landing. Super Admin pasa
 * siempre.
 *
 * EL PAR, NO EL SLUG: dos tipos homónimos en ámbitos distintos tienen
 * accesos independientes (inv_type_access va por asset_type_id, y los
 * gemelos tienen ids distintos). Validar solo por slug dejaba pasar a la
 * pantalla del ámbito interno a quien únicamente tenía acceso al colaborador
 * — y al revés —, y ahí el backend respondía 403 sobre una entrada de menú
 * que él mismo había pintado como navegable.
 *
 * El `scope` llega como prop desde routes.jsx (los segmentos de ámbito son
 * literales, así que no hay un :scope en useParams).
 *
 * Es la pareja de front del backend `requireTypeAccess`: el backend es la
 * autoridad real (devuelve 403), este guard evita que el usuario aterrice en
 * una pantalla que daría error y le da un rebote limpio.
 */
/**
 * Compatibilidad de las rutas SIN ámbito: /inventario/:slug[/:id].
 *
 * POR QUÉ SIGUE EXISTIENDO:
 *   Esas URLs están en favoritos, en enlaces compartidos y —sobre todo—
 *   guardadas en BBDD: los avisos in-app de `notificaciones_inventario`
 *   llevan un `link` con la forma antigua. Romperlas no es una opción.
 *
 * RESOLUCIÓN (contra los tipos NAVEGABLES del usuario, no contra el
 * catálogo completo — ver más abajo):
 *   - el slug existe en un solo ámbito → a ese ámbito
 *   - existe en los dos              → a 'interno', como default EXPLÍCITO
 *   - no existe (o sin acceso)       → a 'interno', que es donde la
 *     pantalla de activos muestra su "no existe en este ámbito"
 *
 * CONTRA LOS NAVEGABLES, Y NO CONTRA EL CATÁLOGO:
 *   `useTiposNavegables` pide /tipos/navegables, que el backend ya filtra
 *   por can_view (Capa 2). Resolver contra el catálogo completo mandaría a
 *   quien solo tiene acceso al gemelo colaborador de un slug duplicado
 *   contra el interno, y el backend le devolvería un 403 — reproduciendo
 *   por la puerta de compatibilidad exactamente el fallo que este arreglo
 *   corrige. Con los navegables, ese usuario aterriza en colab.
 *
 * Se preserva el querystring y se redirige con `replace` para no dejar la
 * URL ambigua en el historial (el botón de atrás volvería a entrar aquí).
 */
export function InvTipoCompat() {
  const { slug, id } = useParams();
  const { ambitosDe, loading } = useTiposNavegables();
  const { search } = useLocation();

  if (loading) {
    return <InvSkeleton />;
  }

  const ambitos = ambitosDe(slug);
  const destino = ambitos.length === 1 ? ambitos[0] : 'interno';

  return <Navigate to={`${rutaTipo(destino, slug, id)}${search || ''}`} replace />;
}

export function InvTipoGuard({ scope, children }) {
  const { superAdmin, permisosCargados } = useInvPermisos();
  const { buscarTipo, loading } = useTiposNavegables();
  const { slug } = useParams();

  if (!permisosCargados || loading) {
    return <InvSkeleton />;
  }
  if (superAdmin) {
    return children;
  }
  if (!buscarTipo(scope, slug)) {
    return <Navigate to="/inventario" replace />;
  }
  return children;
}
